import { FC, ReactNode, MouseEvent } from 'react';
import { Controller, ControllerProps } from 'react-hook-form';
import { InputAdornment, IconButton, Tooltip } from '@mui/material';
import { TextField } from '../index.ts';
import { TTextFieldProps } from '../fields/textField.component.tsx';

export const FormTextFieldWithButton: FC<TFormTextFieldWithButtonProps> = ({
  name,
  onBlur,
  onChange,
  defaultValue,
  controllerProps,
  disabled,
  transformValue,

  buttonIcon,
  buttonAriaLabel = 'action',
  buttonTooltip,
  onButtonClick,
  buttonVisibility = 'always',
  buttonDisabled,

  ...props
}) => {
  return (
    <Controller
      {...controllerProps}
      name={name}
      defaultValue={defaultValue}
      render={({
        field: {
          value,
          onChange: onFieldChange,
          onBlur: onFieldBlur,
          disabled: fieldDisabled,
          ...restField
        },
      }) => {
        let displayedValue: string = (value as string) || '';
        if (transformValue) {
          displayedValue = transformValue(displayedValue);
        }

        const visibilitySx = getVisibilitySx(buttonVisibility);

        const actionButton = (
          <IconButton
            edge='end'
            size='small'
            aria-label={buttonVisibility === 'never' ? '' : buttonAriaLabel}
            onClick={onButtonClick}
            disabled={
              Boolean(buttonDisabled) ||
              (typeof disabled === 'boolean' ? disabled : fieldDisabled) ||
              buttonVisibility === 'never'
            }
            sx={{ p: 0.5, ...visibilitySx }}
          >
            {buttonIcon}
          </IconButton>
        );

        return (
          <TextField
            {...props}
            slotProps={{
              ...props.slotProps,
              input: {
                ...props.slotProps?.input,
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{
                      gap: 0.5,
                      ...(props.multiline && { alignSelf: 'flex-start', mt: '4px' }),
                    }}
                  >
                    {(props.slotProps?.input as Record<string, any>)?.endAdornment}
                    {buttonTooltip ? (
                      <Tooltip title={buttonTooltip} arrow>
                        {actionButton}
                      </Tooltip>
                    ) : (
                      actionButton
                    )}
                  </InputAdornment>
                ),
              },
            }}
            value={displayedValue}
            onChange={(e) => {
              onFieldChange(e.target.value ? e : null);
              onChange?.(e.target.value);
            }}
            onBlur={(e) => {
              onFieldBlur();
              onBlur?.(e);
            }}
            disabled={typeof disabled === 'boolean' ? disabled : fieldDisabled}
            {...restField}
          />
        );
      }}
    />
  );
};

export type TFormTextFieldWithButtonProps = Omit<TTextFieldProps, 'value' | 'onChange'> & {
  name: ControllerProps['name'];
  onChange?: (v: string) => void;
  controllerProps?: Omit<ControllerProps, 'name' | 'render'>;
  disabled?: boolean;
  transformValue?: (v: string) => string;
  buttonIcon?: ReactNode;
  buttonAriaLabel?: string;
  buttonTooltip?: string;
  onButtonClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  buttonVisibility?: 'always' | 'hover' | 'focus' | 'hoverOrFocus' | 'never';
  buttonDisabled?: boolean;
};

function getVisibilitySx(
  mode: TFormTextFieldWithButtonProps['buttonVisibility'],
): Record<string, any> {
  const focusVisible = {
    '&:focus': { opacity: 1 },
    '&.Mui-focusVisible': { opacity: 1 },
  } as const;

  const base = { transition: 'opacity 120ms ease' } as const;
  switch (mode) {
    case 'always':
      return { ...base, opacity: 1 };
    case 'never':
      return { ...base, opacity: 0 };
    case 'hover':
      return {
        ...base,
        opacity: 0,
        '.MuiInputBase-root:hover &': { opacity: 1 },
        ...focusVisible,
      };
    case 'focus':
      return {
        ...base,
        opacity: 0,
        '.MuiInputBase-root.Mui-focused &': { opacity: 1 },
        ...focusVisible,
      };
    case 'hoverOrFocus':
      return {
        ...base,
        opacity: 0,
        '.MuiInputBase-root:hover &, .MuiInputBase-root.Mui-focused &': { opacity: 1 },
        ...focusVisible,
      };
    default:
      return { ...base, opacity: 1 };
  }
}
